import React, { useState, useEffect, FormEvent } from 'react';
import './App.css';
import logoImage from './assets/logo_truly_transparent.png';
import styled from 'styled-components';
import Gallery from './components/Gallery/Gallery';

// Import all images from the gallery folder for feature boxes
import aiPowered from './assets/gallery/ai_powered.png';
import flexible from './assets/gallery/flexible.png';
import trinity from './assets/gallery/trinity.png';
import explore from './assets/gallery/explore.png';
import offline from './assets/gallery/offline.png';
import artstyle from './assets/gallery/artstyle.png';

const SuccessMessage = styled.div`
  position: relative;
  background-color: rgba(39, 174, 96, 0.2);
  border: 1px solid #27ae60;
  color: #27ae60;
  padding: 1rem;
  border-radius: 4px;
  text-align: center;
  width: fit-content;
  margin: 1rem auto;
`;

const ErrorMessage = styled.div`
  position: relative;
  background-color: rgba(233, 69, 96, 0.2);
  border: 1px solid #e94560;
  color: #e94560;
  padding: 1rem;
  border-radius: 4px;
  text-align: center;
  width: fit-content;
  margin: 1rem auto;
`;

function App() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitionStatus, setSubmissionStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [featureImage, setFeatureImage] = useState('');
  const [isFeatureImageOpen, setIsFeatureImageOpen] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    setIsSubmitting(true)

    if (email && email.includes('@')) {
      const options = {
        method: 'POST',
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
        },
        body: JSON.stringify({email: email})
      };

      setEmail('');

      try {
        const response = await fetch('https://us-central1-code-name-side.cloudfunctions.net/subscribeNewsletter', options);
        //const response = { ok: true, status: 200 }; // Mock response for testing

        setIsSubmitting(false);

        console.log(response);

        if (!response.ok && response.status !== 400) {
          throw new Error('Newsletter subscription failed');
        }

        setIsSubmitting(false);
        setSubmissionStatus('success');

        // setTimeout(() => {
        //   setSubmissionStatus('idle');
        // }, 5000);
      }
      catch (error) {
        setIsSubmitting(false);
        setSubmissionStatus('error');

        setTimeout(() => {
          setSubmissionStatus('idle');
        }, 5000);
      }
    }
    else {
      setIsSubmitting(false);
    }
  };

  // Function to open feature image in full screen
  const openFeatureImage = (src: string) => {
    setFeatureImage(src);
    setIsFeatureImageOpen(true);
  };

  // Function to close feature image
  const closeFeatureImage = () => {
    setIsFeatureImageOpen(false);
  };

  useEffect(() => {
    // Create hexagon background animation
    const createHexagons = () => {
      const hero = document.querySelector('.hero');
      if (!hero) return;
      
      // Clear any existing hexagons
      const existingHexagons = document.querySelectorAll('.hexagon-bg');
      existingHexagons.forEach(hex => hex.remove());
      
      // Create container for hexagons
      const hexContainer = document.createElement('div');
      hexContainer.className = 'hexagon-container';
      hero.appendChild(hexContainer);
      
      // Create multiple hexagons
      for (let i = 0; i < 15; i++) {
        const hexagon = document.createElement('div');
        hexagon.className = 'hexagon-bg';
        
        // Random position
        const posX = Math.random() * 100;
        const posY = Math.random() * 100;
        
        // Random size between 50px and 150px
        const size = 50 + Math.random() * 100;
        
        // Random opacity between 0.1 and 0.3
        const opacity = 0.1 + Math.random() * 0.2;
        
        // Random animation duration between 20s and 60s
        const duration = 20 + Math.random() * 40;
        
        // Random delay between 0s and 10s
        const delay = Math.random() * 10;
        
        // Apply styles
        hexagon.style.left = `${posX}%`;
        hexagon.style.top = `${posY}%`;
        hexagon.style.width = `${size}px`;
        hexagon.style.height = `${size}px`;
        hexagon.style.opacity = opacity.toString();
        hexagon.style.animationDuration = `${duration}s`;
        hexagon.style.animationDelay = `${delay}s`;
        
        hexContainer.appendChild(hexagon);
      }
    };
    
    createHexagons();
    
    // Recreate hexagons on window resize
    window.addEventListener('resize', createHexagons);
    
    return () => {
      window.removeEventListener('resize', createHexagons);
    };
  }, []);

  return (
    <div id="top" className="App">
      <div className="hero">
        <div className="hero-content">
          <a href="#top">
            <img src={logoImage} alt="Dominus Automa Game Logo" className="hero-logo" />
          </a>
          <h1 className="hero-title">Hero Manager Auto-grinder</h1>
          <p className="hero-subtitle">
            A new adventure for ex-MMO players who don't have enough time to play the games they love
          </p>
          <div className="hero-cta">
            <a href="https://store.steampowered.com/app/3795810/Dominus_Automa/" className="btn btn-steam">
              <div className="steam-icon">
              </div>
              <div className="steam-text-wrapper">
                <div className="steam-text-line-1">Wishlist</div>
                <div className="steam-text-line-2">on Steam ™</div>
              </div>
            </a>
          </div>
          <div className="hero-buttons">
            <a href="https://discord.gg/8MASx8aNSv" className="btn btn-discord">
              <div className="discord-icon">
              </div>
              Discord
            </a>
            <a href="#newsletter" className="btn btn-newsletter">
              <div className="newsletter-icon">
              </div>
              Newsletter
            </a>
          </div>
        </div>
      </div>

      {/* Trailer Section - Moved from Gallery */}
      <section id="trailer" className="trailer-section">
        <h2 className="trailer-title">Game Trailer</h2>
        <div className="video-container">
          <iframe 
            src="https://www.youtube.com/embed/UAZhk-8Mu3U" 
            title="Dominus Automa - Trailer"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          ></iframe>
        </div>
      </section>

      <section id="features" className="features">
        <h2 className="features-title">Game Features</h2>
        <div className="features-grid">
          <div className="feature-card">
            <div className="feature-icon">🎮</div>
            <h3 className="feature-title">AI-Powered Gameplay</h3>
            <p className="feature-description">
              Command your character using voice commands, setup different combat presets, and configure your character's behavior.
            </p>
            <div className="feature-image" onClick={() => openFeatureImage(aiPowered)}>
              <img src={aiPowered} alt="AI-Powered Gameplay" />
            </div>
          </div>
          <div className="feature-card">
            <div className="feature-icon">👥</div>
            <h3 className="feature-title">Flexible Play Styles</h3>
            <p className="feature-description">
              Enjoy solo play, group play, raids, and PvP. Join guilds and parties to experience the full depth of the MMORPG experience.
            </p>
            <div className="feature-image" onClick={() => openFeatureImage(flexible)}>
              <img src={flexible} alt="Flexible Play Styles" />
            </div>
          </div>
          <div className="feature-card">
            <div className="feature-icon">⚔️</div>
            <h3 className="feature-title">Holy Trinity System</h3>
            <p className="feature-description">
              Choose your role as Tank, Healer, or DPS in the classic MMO trinity system, with AI assistance to perfect your gameplay.
            </p>
            <div className="feature-image" onClick={() => openFeatureImage(trinity)}>
              <img src={trinity} alt="Holy Trinity System" />
            </div>
          </div>
          <div className="feature-card">
            <div className="feature-icon">🌍</div>
            <h3 className="feature-title">Rich World Interaction</h3>
            <p className="feature-description">
              Gather resources, craft items, and explore a vibrant low-poly world from a 3D top-down perspective.
            </p>
            <div className="feature-image" onClick={() => openFeatureImage(explore)}>
              <img src={explore} alt="Rich World Interaction" />
            </div>
          </div>
          <div className="feature-card">
            <div className="feature-icon">⏱️</div>
            <h3 className="feature-title">Play On Your Schedule</h3>
            <p className="feature-description">
              The game works even when you're offline. Set goals and let your AI hero work towards them while you're away.
            </p>
            <div className="feature-image" onClick={() => openFeatureImage(offline)}>
              <img src={offline} alt="Play On Your Schedule" />
            </div>
          </div>
          <div className="feature-card">
            <div className="feature-icon">🎨</div>
            <h3 className="feature-title">Low-Poly Art Style</h3>
            <p className="feature-description">
              Experience a beautiful 3D top-down world with a distinctive low-poly art style.
            </p>
            <div className="feature-image" onClick={() => openFeatureImage(artstyle)}>
              <img src={artstyle} alt="Low-Poly Art Style" />
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section - Now without the trailer */}
      <Gallery />

      <section id="newsletter" className="newsletter">
        <h2 className="newsletter-title">Stay Updated</h2>
        <p className="newsletter-description">
          Subscribe to our newsletter to receive the latest news, updates, and early access opportunities for Dominus Automa
        </p>
        <form className="newsletter-form" onSubmit={handleSubmit}>
          <input
            type="email"
            className="newsletter-input"
            placeholder="Your email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <button type="submit" className="newsletter-button" disabled={isSubmitting}>
            {isSubmitting ? 'Subscribing...' : 'Subscribe'}
          </button>
        </form>

        {submitionStatus === 'success' && (
          <SuccessMessage>
            <span>Thank you for subscribing! We'll keep you updated on all things related to Dominus Automa.</span>
            <button className='close-submission' onClick={() => setSubmissionStatus('idle')}><i className='close-icon'></i></button>
          </SuccessMessage>
        )}
        
        {submitionStatus === 'error' && (
          <ErrorMessage>
            Error occurred, please try again later.
          </ErrorMessage>
        )}
      </section>

      {/* Feature Image Modal */}
      {isFeatureImageOpen && (
        <div className="feature-modal" onClick={closeFeatureImage}>
          <div className="feature-modal-content" onClick={(e) => e.stopPropagation()}>
            <img src={featureImage} alt="Feature detail" />
            <button className="close-button" onClick={closeFeatureImage}>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
        </div>
      )}

      <footer className="footer" id="footer">
        <a href="#top">
          <img src={logoImage} alt="Dominus Automa Game Logo" className="footer-logo" />
        </a>
        <div className="footer-links">
          <a href="#features" className="footer-link">Features</a>
          <a href="#gallery" className="footer-link">Gallery</a>
          <a href="#newsletter" className="footer-link">Newsletter</a>
          <a href="https://discord.gg/8MASx8aNSv" className="footer-link" target="_blank" rel="noopener noreferrer">Discord</a>
          <a href="https://store.steampowered.com/app/3795810/Dominus_Automa/" className="footer-link" target="_blank" rel="noopener noreferrer">Steam</a>
        </div>
        <div className="footer-social">
          <a href="https://x.com/FERAGames_" className="footer-social-icon" target="_blank" rel="noopener noreferrer" aria-label="X (Twitter)">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 4s-.7 2.1-2 3.4c1.6 10-9.4 17.3-18 11.6 2.2.1 4.4-.6 6-2C3 15.5.5 9.6 3 5c2.2 2.6 5.6 4.1 9 4-.9-4.2 4-6.6 7-3.8 1.1 0 3-1.2 3-1.2z"></path>
            </svg>
          </a>
          <a href="https://www.facebook.com/people/Dominus-Automa/61576791945341/" className="footer-social-icon" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path>
            </svg>
          </a>
          <a href="https://www.instagram.com/dominus.automa/" className="footer-social-icon" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
              <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
              <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
            </svg>
          </a>
        </div>
        <p className="footer-copyright">
          &copy; {new Date().getFullYear()} Dominus Automa Game. All rights reserved.
        </p>
      </footer>
    </div>
  );
}

export default App;
